import React, { useEffect } from 'react';
import { Eye, TrendingUp, CheckCircle, ShieldCheck, Activity, Zap } from 'lucide-react';

const ProblemSolution = () => {
    useEffect(() => {
        const orbitRing = document.getElementById('solution-orbit-ring');
        if (!orbitRing) return;

        let currentRotation = 0;
        let targetRotation = 0;
        const rotationSpeed = 0.15;
        let animationFrameId;

        function updateRotation() {
            const scrollY = window.scrollY;
            targetRotation = scrollY * rotationSpeed;
            currentRotation += (targetRotation - currentRotation) * 0.1;

            orbitRing.style.transform = `rotate(${currentRotation}deg)`;

            const icons = orbitRing.children;
            for (let i = 0; i < icons.length; i++) {
                if (icons[i].tagName === 'STYLE') continue;
                const baseRotation = i * 60;
                icons[i].style.transform = `rotate(${baseRotation}deg) translateY(var(--orbit-radius, -320%)) rotate(${-baseRotation - currentRotation}deg)`;
            }

            animationFrameId = requestAnimationFrame(updateRotation);
        }

        animationFrameId = requestAnimationFrame(updateRotation);

        const xlCells = document.querySelectorAll('.xl-cell');
        let highlightTimeoutIds = [];
        if (xlCells.length > 0) {
            function triggerRandomHighlight() {
                const randomCell = xlCells[Math.floor(Math.random() * xlCells.length)];

                randomCell.style.backgroundColor = 'rgba(119, 174, 197, 0.25)';
                randomCell.style.outline = '1px solid #77AEC5';
                randomCell.style.outlineOffset = '-1px';

                highlightTimeoutIds.push(setTimeout(() => {
                    if (randomCell.hasAttribute('data-base')) {
                        const baseValue = parseInt(randomCell.getAttribute('data-base'));
                        const fluctuation = 1 + (Math.random() * 0.04 - 0.01);
                        const newValue = Math.round(baseValue * fluctuation);
                        randomCell.innerText = '$' + newValue.toLocaleString();

                        randomCell.style.color = '#77AEC5';
                        highlightTimeoutIds.push(setTimeout(() => randomCell.style.color = '', 400));
                    }
                }, 1200));

                highlightTimeoutIds.push(setTimeout(() => {
                    randomCell.style.backgroundColor = 'transparent';
                    randomCell.style.outline = 'none';
                }, 2500));

                const nextTick = 500 + Math.random() * 1500;
                highlightTimeoutIds.push(setTimeout(triggerRandomHighlight, nextTick));
            }

            triggerRandomHighlight();
        }

        return () => {
            if (animationFrameId) cancelAnimationFrame(animationFrameId);
            highlightTimeoutIds.forEach(clearTimeout);
        }
    }, []);

    const iconsList = [Eye, TrendingUp, CheckCircle, ShieldCheck, Activity, Zap];

    return (
        <>
            <section className="section-padding-lg bg-primary-light overflow-hidden border-t border-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                    <div className="flex flex-col-reverse lg:flex-row items-center gap-16 md:mb-40">
                        <div className="w-full lg:w-[45%]" data-aos="fade-right">
                            <p className="label-md mb-4 uppercase tracking-widest">The Solution</p>
                            <h2 className="heading-lg mb-6">
                                Radical visibility that eliminates the "black hole."
                            </h2>
                            <p className="paragraph-lg">
                                Trustpoint handles the complexity of the revenue cycle by providing total transparency into every
                                claim. With real-time tracking and a proactive, clinician-led mindset, we eliminate the uncertainty
                                that slows your practice down.
                            </p>
                            <div className="mt-10">
                                <a href="#contact" className="btn-primary-lg">Discover Radical
                                    Visibility</a>
                            </div>
                        </div>

                        <div className="w-full lg:w-[55%] flex justify-center lg:justify-end" data-aos="fade-left">
                            <div
                                className="relative w-full max-w-[500px] aspect-square flex items-center justify-center scale-90 sm:scale-100">

                                <div className="absolute inset-4 sm:inset-4 md:inset-3 lg:inset-2 rounded-full bg-primary/40">
                                </div>

                                <div id="solution-orbit-ring" className="absolute inset-4 sm:inset-4 md:inset-3 lg:inset-2 transition-transform duration-75 ease-out">
                                    {iconsList.map((Icon, i) => {
                                        const deg = i * 60;
                                        return (
                                            <div key={i} className="absolute top-1/2 left-1/2 -ml-5 -mt-5 sm:-ml-6 sm:-mt-6 w-10 h-10 sm:w-12 sm:h-12 bg-primary-light rounded-full border border-gray-100 flex items-center justify-center text-accent transition-all duration-200"
                                                style={{ transform: `rotate(${deg}deg) translateY(var(--orbit-radius, -200%)) rotate(-${deg}deg)` }}>
                                                <Icon className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth="1.2" />
                                            </div>
                                        )
                                    })}
                                    <style>{`
                                        :root { --orbit-radius: -280%; }
                                        @media (min-width: 640px) { :root { --orbit-radius: -380%; } }
                                    `}</style>
                                </div>

                                <div
                                    className="relative z-10 w-64 h-64 sm:w-64 sm:h-64 md:w-64 md:h-64 lg:w-72 lg:h-72 flex items-center justify-center border border-primary rounded-full p-6 sm:p-8 bg-primary-light uppercase tracking-widest font-serif">
                                    <img src="/assets/images/logo.png" alt="Trustpoint" className="w-full h-auto object-contain" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-24 bg-primary overflow-hidden border-t border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row items-center gap-16">
                        <div className="w-full lg:w-[50%] flex justify-center" data-aos="fade-right">
                            <div className="relative w-full max-w-[440px] aspect-square">
                                <div className="absolute inset-0 bg-primary-light rounded-[2rem] overflow-hidden">
                                    <div className="absolute inset-0 pt-8 px-6 sm:px-10 flex flex-col gap-0.5 opacity-70">
                                        <div className="grid grid-cols-4 gap-4 pb-2 border-b border-gray-200/60 mb-2">
                                            <div className="text-sm uppercase tracking-wider text-gray-400 font-medium px-2">Claim ID</div>
                                            <div className="text-sm uppercase tracking-wider text-gray-400 font-medium px-2">Provider</div>
                                            <div className="text-sm uppercase tracking-wider text-gray-400 font-medium text-right px-2">Billed</div>
                                            <div className="text-sm uppercase tracking-wider text-gray-400 font-medium text-right px-2">Recovered</div>
                                        </div>
                                        {[
                                            { id: '#72410', provider: 'Psychiatry', billed: 12500, recovered: 14200 },
                                            { id: '#72411', provider: 'Orthopedic', billed: 8400, recovered: 9150 },
                                            { id: '#72412', provider: 'Cardiology', billed: 24000, recovered: 27800 },
                                            { id: '#72413', provider: 'General', billed: 16700, recovered: 18900 },
                                            { id: '#72414', provider: 'Internal', billed: 5300, recovered: 6100 },
                                            { id: '#72415', provider: 'Surgery', billed: 31200, recovered: 35400 },
                                            { id: '#72416', provider: 'Emergency', billed: 18200, recovered: 21500 },
                                            { id: '#72417', provider: 'Psychiatry', billed: 14500, recovered: 16100 },
                                            { id: '#72418', provider: 'Orthopedic', billed: 9200, recovered: 10400 },
                                            { id: '#72419', provider: 'Neuro', billed: 42000, recovered: 48500 },
                                            { id: '#72420', provider: 'General', billed: 7800, recovered: 8600 }
                                        ].map((row, i) => (
                                            <div key={i} className="grid grid-cols-4 gap-4 border-b border-gray-200/40 relative">
                                                <div className="text-sm text-gray-400 font-sans xl-cell px-2 py-2">{row.id}</div>
                                                <div className="text-sm text-gray-400 font-sans xl-cell px-2 py-2">{row.provider}</div>
                                                <div className="text-sm text-gray-500 text-right xl-cell font-sans px-2 py-2" data-base={row.billed}>
                                                    ${row.billed.toLocaleString()}
                                                </div>
                                                <div className="text-sm text-secondary font-medium text-right xl-cell font-sans px-2 py-2" data-base={row.recovered}>
                                                    ${row.recovered.toLocaleString()}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
                                </div>
                                <div className="absolute z-10 right-[-60px] bottom-0 pointer-events-none">
                                    <img className="max-w-[200px] h-auto -scale-x-100 lg:scale-x-100" src="/assets/images/women.png" alt="Clinician Success" />
                                </div>
                            </div>
                        </div>

                        <div className="w-full lg:w-[50%]" data-aos="fade-left">
                            <p className="label-md mb-4 uppercase tracking-widest">The Result</p>
                            <h2 className="heading-lg mb-6">
                                Shared stewardship that lets you reclaim your time.
                            </h2>
                            <p className="paragraph-lg">
                                When you pair big-company capabilities with our concierge-style communication, clinicians can stop
                                acting as clerks and start reclaiming their lives. Our shared stewardship approach ensures every
                                dollar is tracked and recovered, with 90% of claims submitted within just 3 days of the DOS.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default ProblemSolution;
